import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material'
import { Table, TableColumnsType, Tag } from 'antd'
import dayjs from 'dayjs'
import {
  IOrder,
  EOrderStatus,
  EOrderType,
  IOrderItem,
} from '../../../../../types/order'
import {
  ONLY_DATE_FORMAT,
  ONLY_DATE_TIME_FORMAT,
} from '../../../../../constants/global'
import OrderTag from '../../../../../components/OrderTag'

interface OrderDetailModalProps {
  isOpen: boolean
  onClose: () => void
  order: IOrder | null
}

const OrderDetailModal = ({
  isOpen,
  onClose,
  order,
}: OrderDetailModalProps) => {
  if (!order) return null

  const productColumns: TableColumnsType<IOrderItem> = [
    // {
    //   title: 'ลำดับ',
    //   dataIndex: 'index',
    //   key: 'index',
    //   width: 80,
    //   align: 'center',
    //   render: (_: any, __: any, index: number) => index + 1,
    // },
    {
      title: 'รูปสินค้า',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 100,
      align: 'center',
      render: (imageUrl: string) => (
        <img
          src={imageUrl}
          alt="product"
          style={{
            width: '60px',
            height: '60px',
            objectFit: 'cover',
            borderRadius: '4px',
          }}
        />
      ),
    },
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'productName',
      key: 'productName',
      width: 200,
      render: (_, item) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{item.productName}</div>
          {item.isCollection && (
            <div style={{ fontSize: '0.9em', color: '#666' }}>
              {item.collections?.map((collection, idx) => (
                <div key={idx}>
                  {collection.productName} x {collection.quantity}
                </div>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      key: 'productCode',
      width: 120,
    },
    {
      title: 'ราคา',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      align: 'right',
      render: (price: number) => price.toLocaleString(),
    },
    {
      title: 'จำนวน',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center',
    },
    {
      title: 'ส่วนลด',
      dataIndex: 'discount',
      key: 'discount',
      width: 80,
      align: 'right',
      render: (discount: number) => discount.toLocaleString(),
    },
    {
      title: 'รวม',
      dataIndex: 'total',
      key: 'total',
      width: 80,
      align: 'right',
      render: (total: number) => total.toLocaleString(),
    },
  ]

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h6">
              รายละเอียดคำสั่งซื้อ #{order.code}
            </Typography>
          </Grid>
          <Grid item>
            <OrderTag order={order} status={order.status as EOrderStatus} />
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* ข้อมูลลูกค้า */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              ข้อมูลลูกค้า
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">
                  ชื่อ: {order.recipient.name}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">
                  เบอร์โทร: {order.recipient.phone}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    wordBreak: 'break-word',
                  }}
                >
                  ที่อยู่: {order.recipient.address}{' '}
                  {order.recipient.subDistrict} {order.recipient.district}{' '}
                  {order.recipient.province} {order.recipient.zipcode}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* ข้อมูลการชำระเงิน */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              ข้อมูลการชำระเงิน
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">
                  วิธีการชำระเงิน:{' '}
                  <Tag
                    color={
                      order.paymentMethod === EOrderType.COD
                        ? 'error'
                        : 'success'
                    }
                  >
                    {order.paymentMethod === EOrderType.COD
                      ? 'เก็บเงินปลายทาง'
                      : 'โอนผ่านธนาคาร'}
                  </Tag>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">
                  ยอดรวม: {order.total.toLocaleString()} บาท
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">
                  วันที่สั่งซื้อ:{' '}
                  {order.orderDate
                    ? dayjs(order.orderDate).format(ONLY_DATE_FORMAT)
                    : '-'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* รายการสินค้า */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              รายการสินค้า
            </Typography>
            <Table
              columns={productColumns}
              dataSource={order.items}
              pagination={false}
              size="small"
              bordered
              //   scroll={{ x: 1000 }}
            />
          </Grid>

          {/* หมายเหตุ */}
          {order.note && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                หมายเหตุ
              </Typography>
              <Typography variant="subtitle2">{order.note}</Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

export default OrderDetailModal
